import React from 'react';

/**
 * Shadow thumbnails — rendered-style SVGs that show the SHADOW each
 * option produces under a shared subject (sphere on a floor). Same
 * 120×90 stage as the lighting set, same sphere geometry, same palette
 * so the vendor can scan shadow/lighting cards side-by-side and stay
 * oriented.
 *
 * Design rules:
 *   - Sphere centered at (60, 42), radius 22. Horizon at y=62.
 *   - Subject is neutral / soft-lit the SAME WAY across all four
 *     variants — only the shadow (and floor treatment for "reflection")
 *     differs. Keeps the comparison pure.
 *   - Gradient IDs are scoped per variant to avoid collisions when
 *     multiple cards render on the canvas.
 */

const baseStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4/3',
    display: 'block',
};

const SPHERE_CX = 60;
const SPHERE_CY = 42;
const SPHERE_R = 22;
const HORIZON = 62;

const Floor: React.FC<{ fill: string }> = ({ fill }) => (
    <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill={fill} />
);

/**
 * Shared sphere rendering — identical soft-lit look across every
 * shadow variant so the comparison isolates the shadow treatment.
 * Gradient id is parameterized so each card owns its own scoped def.
 */
const Sphere: React.FC<{ gradientId: string }> = ({ gradientId }) => (
    <>
        <defs>
            <radialGradient id={gradientId} cx="42%" cy="30%" r="80%">
                <stop offset="0%" stopColor="#EFEEEB" />
                <stop offset="55%" stopColor="#A8A7A4" />
                <stop offset="100%" stopColor="#4A4945" />
            </radialGradient>
        </defs>
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill={`url(#${gradientId})`} />
    </>
);

// ─── natural soft shadow ──────────────────────────────────────────
// Gentle feathered oval under the sphere — the default "ambient
// contact" shadow you get with a softbox or natural daylight.
export const NaturalSoftShadow: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <radialGradient id="sh-soft-shadow" cx="50%" cy="50%" r="55%">
                <stop offset="0%" stopColor="#1F2022" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#1F2022" stopOpacity="0" />
            </radialGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        <ellipse cx={SPHERE_CX} cy={HORIZON + 2} rx={SPHERE_R + 8} ry={6} fill="url(#sh-soft-shadow)" />
        <Sphere gradientId="sh-soft-sphere" />
    </svg>
);

// ─── hard dramatic shadow ─────────────────────────────────────────
// Sharp-edged elongated shadow raking across the floor — golden-hour
// or hard side-key feel.
export const HardDramaticShadow: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        {/* long hard-edged shadow raking right */}
        <path
            d={`M ${SPHERE_CX - 2} ${HORIZON + 1}
                L 118 ${HORIZON + 3}
                L 114 ${HORIZON + 7}
                L ${SPHERE_CX - 6} ${HORIZON + 7} Z`}
            fill="#1F2022"
            opacity="0.55"
        />
        <Sphere gradientId="sh-hard-sphere" />
    </svg>
);

// ─── no shadow ────────────────────────────────────────────────────
// Sphere floats above a seamless white backdrop — the "cut-out /
// floating" product-catalog look. Horizon intentionally hidden so
// the sphere reads as detached from any ground plane.
export const NoShadow: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        {/* no floor — seamless backdrop continues all the way down */}
        <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill="#F4F4F2" />
        <Sphere gradientId="sh-none-sphere" />
    </svg>
);

// ─── reflection ───────────────────────────────────────────────────
// Glossy mirrored floor — the sphere's lower half reflects downward,
// fading with distance. Replaces a shadow with a reflection, same
// editorial function (grounding the subject) with a different mood.
export const ReflectionShadow: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <radialGradient id="sh-refl-sphere-mirror" cx="42%" cy="30%" r="80%">
                <stop offset="0%" stopColor="#EFEEEB" />
                <stop offset="55%" stopColor="#A8A7A4" />
                <stop offset="100%" stopColor="#4A4945" />
            </radialGradient>
            <linearGradient id="sh-refl-floor" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#EFEDE8" />
                <stop offset="100%" stopColor="#C4C2BD" />
            </linearGradient>
            {/* Fade overlay — transparent at the horizon, opaque floor
                color at the bottom. Painted ON TOP of the reflected
                sphere so the reflection dissolves into the floor with
                distance (proper mirror gloss falloff). Uses fill
                opacity instead of a mask so it renders reliably across
                browsers. */}
            <linearGradient id="sh-refl-fade-overlay" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#C4C2BD" stopOpacity="0" />
                <stop offset="100%" stopColor="#C4C2BD" stopOpacity="1" />
            </linearGradient>
            {/* Clip so the mirrored sphere can't bleed above the
                horizon (its top pixel sits just a few units above y=62
                after the flip and would poke through otherwise). */}
            <clipPath id="sh-refl-clip">
                <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} />
            </clipPath>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        {/* glossy gradient floor */}
        <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill="url(#sh-refl-floor)" />
        {/* mirrored sphere — flipped around horizon, clipped to the
            floor area, then softened by the fade overlay drawn on top.
            Group-level opacity keeps the reflection dimmer than the
            real object. */}
        <g clipPath="url(#sh-refl-clip)" opacity="0.5">
            <g transform={`translate(0, ${2 * HORIZON}) scale(1, -1)`}>
                <circle
                    cx={SPHERE_CX}
                    cy={SPHERE_CY}
                    r={SPHERE_R}
                    fill="url(#sh-refl-sphere-mirror)"
                />
            </g>
        </g>
        {/* fade-to-floor overlay */}
        <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill="url(#sh-refl-fade-overlay)" />
        {/* thin specular highlight line along the horizon */}
        <line x1="0" y1={HORIZON} x2="120" y2={HORIZON} stroke="#FFFFFF" strokeWidth="0.6" opacity="0.7" />
        <Sphere gradientId="sh-refl-sphere-top" />
    </svg>
);

export const SHADOW_ILLUSTRATIONS: Record<string, React.FC> = {
    'natural-soft': NaturalSoftShadow,
    'hard-dramatic': HardDramaticShadow,
    'none': NoShadow,
    'reflection': ReflectionShadow,
};
